import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { MediaLightbox } from './MediaLightbox.js';
import type { LightboxPayload } from './types.js';

type RichMediaContextValue = {
  openLightbox: (payload: LightboxPayload) => void;
  closeLightbox: () => void;
};

const RichMediaContext = createContext<RichMediaContextValue | null>(null);

export function RichMediaProvider({ children }: { children: ReactNode }) {
  const [lightbox, setLightbox] = useState<LightboxPayload | null>(null);

  const openLightbox = useCallback((payload: LightboxPayload) => {
    setLightbox(payload);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox(null);
  }, []);

  const value = useMemo(
    () => ({ openLightbox, closeLightbox }),
    [openLightbox, closeLightbox],
  );

  return (
    <RichMediaContext.Provider value={value}>
      {children}
      {lightbox && (
        <MediaLightbox payload={lightbox} onClose={closeLightbox} />
      )}
    </RichMediaContext.Provider>
  );
}

export function useRichMedia(): RichMediaContextValue {
  const ctx = useContext(RichMediaContext);
  if (!ctx) {
    throw new Error('useRichMedia must be used within RichMediaProvider');
  }
  return ctx;
}

export function useRichMediaOptional(): RichMediaContextValue | null {
  return useContext(RichMediaContext);
}
